import { Component, OnInit, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '@nimmsta/ng-commons-lib/auth';
import { BatteryPredictionBadgeComponent } from '@nimmsta/ng-commons-lib/battery-prediction-badge';
import {
  DateTimePipe,
  NimmstaTimeAgoPipe,
} from '@nimmsta/ng-commons-lib/pipes';
import { DeviceHistoryItem, UserRole } from '@nimmsta/ng-commons-lib/models';
import { DeviceHistoryListComponent } from '@nimmsta/ng-commons-lib/device-history';
import { DeviceHistoryService } from '@nimmsta/ng-commons-lib/services/device-history';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import {
  LightTagPerformanceProfileDisplayKey,
  lightTagPerformanceModeDisplay,
  lightTagPerformanceProfileKey,
} from '@nimmsta/ng-commons-lib/utils/light-tag-performance-mode';
import { LoadingIndicatorComponent } from '@nimmsta/ng-commons-lib/loading-indicator';
import { switchMap, take } from 'rxjs';
import { LightTag } from '../../models/lighttag';
import { LightTagService } from '../../services/lighttag.service';
import { LightTagBatteryPipe } from './lighttag-battery.pipe';
import { LightTagTypePipe } from './lighttag-type.pipe';

type LightTagSection = 'activity' | 'aggregate' | 'software' | 'history';

@Component({
  templateUrl: 'lighttag-detail.component.html',
  styleUrl: 'lighttag-detail.component.scss',
  imports: [
    TranslatePipe,
    HeaderBarComponent,
    LoadingIndicatorComponent,
    ErrorComponent,
    NimmstaTimeAgoPipe,
    DateTimePipe,
    BatteryPredictionBadgeComponent,
    LightTagBatteryPipe,
    LightTagTypePipe,
    DeviceHistoryListComponent,
  ],
})
export class LightTagDetailComponent implements OnInit {
  private lightTagService = inject(LightTagService);
  private deviceHistoryService = inject(DeviceHistoryService);
  private authService = inject(AuthService);
  private route = inject(ActivatedRoute);

  error?: HttpErrorResponse;

  device?: LightTag;

  historyItems: DeviceHistoryItem[] = [];

  isLoading = false;

  isAdmin = false;

  readonly openSections = signal<Record<LightTagSection, boolean>>({
    activity: false,
    aggregate: false,
    software: false,
    history: false,
  });

  readonly aggregateDataFields: Array<{
    key:
    | 'lastSeen'
    | 'lastIlluminated'
    | 'lastLocation'
    | 'lastBatteryStatus'
    | 'isListening'
    | 'uptimeInDays'
    | 'uptimeSinceLastCharge'
    | 'ledOperationTime'
    | 'numberOfConnections';
    label: string
  }> = [
    { key: 'lastSeen', label: 'lighttag.lastSeen' },
    { key: 'lastIlluminated', label: 'lighttag.lastIlluminated' },
    { key: 'lastLocation', label: 'lighttag.lastLocation' },
    { key: 'lastBatteryStatus', label: 'lighttag.lastBatteryStatus' },
    { key: 'isListening', label: 'lighttag.listeningType' },
    { key: 'uptimeInDays', label: 'lighttag.uptimeInDays' },
    { key: 'uptimeSinceLastCharge', label: 'lighttag.uptimeSinceLastCharge' },
    { key: 'ledOperationTime', label: 'lighttag.ledOperationTime' },
    { key: 'numberOfConnections', label: 'lighttag.numberOfConnections' },
  ];

  readonly softwareDataFields: Array<{ key: 'firmwareVersion' | 'hardwareVersion'; label: string }> = [
    { key: 'firmwareVersion', label: 'lighttag.firmwareVersion' },
    { key: 'hardwareVersion', label: 'lighttag.hardwareVersion' },
  ];

  get predictedEmptyAt(): string | Date | undefined {
    return this.device?.batteryPrediction?.predictedEmptyAt ?? undefined;
  }

  get performanceModeDisplay() {
    return this.device
      ? lightTagPerformanceModeDisplay(this.device, this.isAdmin)
      : undefined;
  }

  get headerPerformanceProfileKey(): LightTagPerformanceProfileDisplayKey | undefined {
    return this.device ? lightTagPerformanceProfileKey(this.device) : undefined;
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.authService.getCurrentUser().pipe(
      take(1),
      switchMap((user) => {
        this.isAdmin = user.role === UserRole.ADMIN;
        return this.route.params;
      }),
    ).subscribe((params) => {
      const deviceId = params['id'];
      this.historyItems = [];

      this.lightTagService.get(deviceId).subscribe({
        next: (device: LightTag) => {
          this.device = device;
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.error = error;
          this.isLoading = false;
        },
      });

      if (this.isAdmin) {
        this.deviceHistoryService
          .getLightTagHistory(deviceId, true)
          .subscribe((historyItems) => {
            this.historyItems = historyItems;
          });
      }
    });
  }

  hasAggregateData(): boolean {
    return !!this.performanceModeDisplay
      || this.aggregateDataFields.some((field) => this.hasDisplayValue(this.device?.[field.key]));
  }

  hasSoftwareData(): boolean {
    return this.softwareDataFields.some((field) => this.hasDisplayValue(this.device?.[field.key]));
  }

  hasDisplayValue(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  performanceParameterValue(value: number | null | undefined, unit: string): string {
    return this.hasDisplayValue(value) ? `${value} ${unit}` : '-';
  }

  performanceProfileTranslationKey(profileKey: LightTagPerformanceProfileDisplayKey): string {
    return `lighttag.performanceModeValues.${profileKey}`;
  }

  isSectionOpen(section: LightTagSection): boolean {
    return this.openSections()[section];
  }

  toggleSection(section: LightTagSection): void {
    this.openSections.update((sections) => ({
      ...sections,
      [section]: !sections[section],
    }));
  }
}
